import * as React from 'react';
import { useSelector } from 'react-redux';
import { Responsive } from '@cian/ui-kit';

import { GalleryCell } from '../../components/NewbuildingsComparePage/parts/Cell/cells';
import { IGalleryCell, INewbuildingSummaryCellValue } from '../../types/newbuildingsCompare';
import { selectDeviceType } from '../../selectors/page/selectDeviceType';
import { Content } from '../../components/FullScreenGallery/Content';
import { FullScreenGallery } from '../../components/FullScreenGallery';
import { GalleryHeaderContainer } from '../GalleryHeader';
import { trackOpenFullGallery } from './tracking';
import { MobileGalleryContainer } from '../MobileGallery';
import { AnalyticsContext, IAnalyticsContext } from '../../utils/analyticsContext';

const GALLERY_ANALYTICS_CONTEXT: IAnalyticsContext = { placement: 'gallery' };

export interface IGalleryContainerProps {
  cell: IGalleryCell;
  newbuilding?: INewbuildingSummaryCellValue;
}

export function GalleryContainer({ cell, newbuilding }: IGalleryContainerProps): React.JSX.Element {
  const [isOpened, setOpenedState] = React.useState(false);
  const deviceType = useSelector(selectDeviceType);
  const gallerySiteType = deviceType === 'desktop' ? 'desktop' : 'mobile';
  const hasImages = Boolean(cell.data?.images?.[0]?.fullUrl);

  const onButtonClick = React.useCallback(() => {
    if (!hasImages) {
      return;
    }

    setOpenedState(true);
    trackOpenFullGallery({ newbuildingId: newbuilding?.id, siteType: gallerySiteType });
  }, [gallerySiteType, hasImages, newbuilding?.id]);

  const onClose = React.useCallback(() => {
    setOpenedState(false);
  }, []);

  return (
    <>
      <GalleryCell cell={cell} onOpen={onButtonClick} />
      <AnalyticsContext.Provider value={GALLERY_ANALYTICS_CONTEXT}>
        <Responsive desktop>
          {isOpened && (
            <FullScreenGallery
              header={<GalleryHeaderContainer newbuilding={newbuilding} onClose={onClose} />}
              content={<Content images={cell.data?.images ?? []} />}
              onClose={onClose}
            />
          )}
        </Responsive>
        <Responsive phone tablet>
          {isOpened && (
            <MobileGalleryContainer images={cell.data?.images ?? []} onClose={onClose} newbuilding={newbuilding} />
          )}
        </Responsive>
      </AnalyticsContext.Provider>
    </>
  );
}
